import { motion } from "framer-motion";
import {
  Award,
  Handshake,
  Lightbulb,
  Scale,
  ShieldCheck,
  Users,
} from "lucide-react";
import { FOUNDATIONS, PURPOSE_WORDS, VALUES, WHO_MEDIA } from "../../constants/about";
import { CornerMarks, MediaSlot, useFade } from "./ui";

const ICONS = {
  bulb: Lightbulb,
  shield: ShieldCheck,
  users: Users,
  scale: Scale,
  handshake: Handshake,
  award: Award,
};

function AboutIdentity() {
  const { fade, stagger } = useFade();

  return (
    <>
      <section
        id="who-we-are"
        className="relative scroll-mt-32 overflow-hidden bg-white text-navy"
        aria-labelledby="who-heading"
      >
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <div className="absolute inset-0 bg-[radial-gradient(70%_50%_at_0%_0%,rgba(255,191,24,0.1),transparent_55%),radial-gradient(55%_40%_at_100%_8%,rgba(79,72,236,0.07),transparent_50%)]" />
          <div
            className="absolute inset-0 opacity-[0.32]"
            style={{
              backgroundImage: "radial-gradient(rgba(16,14,52,0.08) 1px, transparent 1px)",
              backgroundSize: "26px 26px",
            }}
          />
        </div>

        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="grid items-stretch gap-12 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:gap-16">
            <motion.div
              className="flex h-full flex-col justify-center"
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              variants={fade}
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
                Who we are
              </p>
              <h2
                id="who-heading"
                className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
              >
                Empowering financial decisions
                <span className="mt-1 block">through innovation</span>
              </h2>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-navy/65 sm:text-lg">
                SimFinance is an African financial technology company dedicated to making financial
                services more transparent, intelligent and accessible.
              </p>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-navy/60">
                Unfortunately, millions of consumers still struggle to understand financial products,
                while financial institutions continue to face challenges acquiring qualified customers,
                managing fraud and recovering outstanding debts.
              </p>
              <p className="mt-4 max-w-xl text-base font-semibold leading-relaxed text-navy">
                SimFinance was created to bridge this gap.
              </p>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-navy/60">
                By combining artificial intelligence, data analytics, digital verification technologies
                and customer engagement tools, we help consumers make better financial decisions while
                enabling financial institutions to grow responsibly and sustainably.
              </p>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-navy/60">
                Today, SimFinance serves as both a technology company and the umbrella ecosystem powering
                multiple intelligent financial platforms that work seamlessly together to improve the
                entire financial services value chain across Africa.
              </p>
            </motion.div>

            <motion.div
              className="relative h-full min-h-[22rem] lg:min-h-0"
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              variants={fade}
            >
              <CornerMarks />
              <div className="relative h-full min-h-[22rem] overflow-hidden border border-navy/10 lg:min-h-full">
                <MediaSlot
                  src={WHO_MEDIA.src}
                  fallback={WHO_MEDIA.fallback}
                  alt={WHO_MEDIA.alt}
                  label={WHO_MEDIA.label}
                  spec={WHO_MEDIA.spec}
                  className="absolute inset-0 h-full w-full"
                />
              </div>
            </motion.div>
          </div>

          <motion.div
            className="mt-16"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-navy/35">
              Every financial decision should be built on
            </p>
            <ol
              className="mt-6 grid gap-px overflow-hidden border border-navy/10 bg-navy/10 lg:grid-cols-3"
              aria-label="Three foundations"
            >
              {FOUNDATIONS.map((item) => (
                <li key={item.title} className="bg-white px-6 py-8 sm:px-8">
                  <span className="font-mono text-[11px] font-semibold tracking-[0.2em] text-gold">
                    {item.index}
                  </span>
                  <p className="mt-5 text-3xl font-semibold tracking-tight text-navy sm:text-4xl">
                    {item.title}
                  </p>
                  <p className="mt-3 max-w-xs text-sm leading-relaxed text-navy/55">{item.copy}</p>
                </li>
              ))}
            </ol>
          </motion.div>
        </div>
      </section>

      <section
        id="our-charter"
        className="relative scroll-mt-32 overflow-hidden bg-[#f7f6fb] text-navy"
        aria-labelledby="charter-heading"
      >
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <div className="absolute inset-0 bg-[radial-gradient(70%_50%_at_100%_0%,rgba(255,191,24,0.12),transparent_55%),radial-gradient(55%_40%_at_0%_20%,rgba(79,72,236,0.07),transparent_50%)]" />
          <div
            className="absolute inset-0 opacity-[0.36]"
            style={{
              backgroundImage: "radial-gradient(rgba(16,14,52,0.08) 1px, transparent 1px)",
              backgroundSize: "26px 26px",
            }}
          />
        </div>

        <div className="relative mx-auto max-w-8xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <motion.header
            className="max-w-3xl"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Our charter
            </p>
            <h2
              id="charter-heading"
              className="mt-4 font-semibold leading-[1.08] tracking-tight text-navy [font-size:clamp(2rem,5vw,3.75rem)]"
            >
              Vision, mission, values
              <span className="mt-1 block">and purpose</span>
            </h2>
          </motion.header>

          <div className="mt-12 grid gap-px overflow-hidden border border-navy/10 bg-navy/10 lg:grid-cols-2">
            <motion.article
              className="relative overflow-hidden bg-navy px-6 py-10 text-white sm:px-10 sm:py-12"
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              variants={fade}
            >
              <div className="pointer-events-none absolute -right-16 top-0 h-48 w-48 rounded-full bg-royal/30 blur-3xl" />
              <div className="pointer-events-none absolute -bottom-16 left-8 h-40 w-40 rounded-full bg-gold/10 blur-3xl" />
              <p className="relative font-mono text-[11px] font-semibold tracking-[0.22em] text-gold">
                01 / Vision
              </p>
              <h3 className="relative mt-6 max-w-lg text-2xl font-semibold leading-snug tracking-tight sm:text-3xl">
                Africa&apos;s most trusted intelligent digital financial ecosystem
              </h3>
              <p className="relative mt-5 max-w-lg text-base leading-relaxed text-white/65">
                To become Africa&apos;s most trusted intelligent digital financial ecosystem that connects
                consumers, financial institutions and technology through innovation, trust and financial
                inclusion.
              </p>
            </motion.article>

            <motion.article
              className="bg-white px-6 py-10 sm:px-10 sm:py-12"
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              variants={fade}
            >
              <p className="font-mono text-[11px] font-semibold tracking-[0.22em] text-gold">
                02 / Mission
              </p>
              <h3 className="mt-6 max-w-lg text-2xl font-semibold leading-snug tracking-tight text-navy sm:text-3xl">
                Simplify financial decision-making
              </h3>
              <p className="mt-5 max-w-lg text-base leading-relaxed text-navy/60">
                To simplify financial decision-making by empowering consumers with trusted information,
                intelligent recommendations and secure digital experiences while helping financial
                institutions acquire customers, reduce risk, improve operational efficiency and achieve
                sustainable growth.
              </p>
            </motion.article>
          </div>

          <motion.div
            className="mt-16"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={stagger}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-navy/35">
              Our core values
            </p>
            <ul className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {VALUES.map((value) => {
                const Icon = ICONS[value.icon];
                return (
                  <motion.li
                    key={value.title}
                    className="border border-navy/10 bg-white px-5 py-6"
                    variants={fade}
                  >
                    <span className="inline-flex h-10 w-10 items-center justify-center bg-navy text-gold">
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <h3 className="mt-4 text-lg font-semibold tracking-tight text-navy">{value.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-navy/55">{value.copy}</p>
                  </motion.li>
                );
              })}
            </ul>
          </motion.div>

          <motion.div
            className="mt-16"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fade}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-navy/35">
              Our purpose
            </p>
            <p className="mt-4 text-xl font-semibold tracking-tight text-navy sm:text-2xl">
              We believe financial services should be:
            </p>
            <ul className="mt-6 flex flex-wrap gap-2" aria-label="Purpose principles">
              {PURPOSE_WORDS.map((word, index) => (
                <li
                  key={word}
                  className="inline-flex items-center gap-2 border border-navy/10 bg-white px-4 py-2.5"
                >
                  <span className="font-mono text-[11px] font-semibold tracking-[0.16em] text-gold">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span className="text-base font-semibold tracking-tight text-navy">{word}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 max-w-3xl text-base leading-relaxed text-navy/60 sm:text-lg">
              Our purpose is to make financial services easier to discover, understand, compare, access
              and manage through technology that benefits everyone in the financial ecosystem.
            </p>
          </motion.div>
        </div>
      </section>
    </>
  );
}

export default AboutIdentity;
